import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useDismissable } from '../layout/useDismissable';

export interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  actions?: ReactNode;
  /** Optional glyph shown before the heading - used by the AI dialog's blue header. */
  icon?: ReactNode;
  className?: string;
  /** Extra class on the scrim layer, for a dialog that changes the layer's padding band. */
  layerClassName?: string;
  closeLabel?: string;
  onClose: () => void;
}

export function Modal({
  open,
  title,
  description,
  children,
  actions,
  icon,
  className = '',
  layerClassName = '',
  closeLabel = 'Đóng',
  onClose,
}: ModalProps) {
  const modalRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();
  // Escape and tap-outside both live in the hook; this effect keeps the focus trap only
  const dismiss = useDismissable(open, onClose);

  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const modal = modalRef.current;
    if (modal) modal.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !modal) return;
      const matches = modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const focusable = Array.prototype.slice.call(matches) as HTMLElement[];
      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === modal)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === modal)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
      if (previousFocus && document.body.contains(previousFocus)) previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;

  /**
   * Portaled to <body>, and it has to be.
   *
   * Four of these modals are raised from inside `.panel`, which is `position: absolute` with
   * `z-index: var(--z-chrome)` and therefore CREATES A STACKING CONTEXT. `--z-modal` (40) then
   * only outranks `.panel`'s own children; against anything outside it the whole modal, scrim
   * included, ranks at 10 - so `.wb-toolbar` and `.quiz-rail` (both `--z-toolbar`, 20) painted
   * over it. Escaping to <body> puts the layer back in the root stacking context, where 40 means
   * 40. Same pattern AiTabbedModal already uses.
   *
   * Nothing else moves: `.modal-layer` is `position: fixed`, so the board neither scrolls nor
   * shifts, and the tokens and type it reads come from `:root` and `body`, both still ancestors.
   */
  const layer = (
    <div className={`modal-layer ${layerClassName}`.trim()} role="presentation" {...dismiss}>
      <section
        ref={modalRef}
        className={`modal ${className}`.trim()}
        role="dialog"
        aria-modal={true}
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="modal__header">
          {icon ? <span className="modal__icon" aria-hidden={true}>{icon}</span> : null}
          <div className="modal__heading">
            <h2 id={titleId} className="modal__title">{title}</h2>
            {description ? <p id={descriptionId} className="modal__description">{description}</p> : null}
          </div>
          <button type="button" className="modal__close" aria-label={closeLabel} onClick={onClose}>×</button>
        </header>
        <div className="modal__body">{children}</div>
        {actions ? <footer className="modal__actions">{actions}</footer> : null}
      </section>
    </div>
  );

  // no document during SSR or a pre-hydration render; rendering nothing beats throwing
  if (typeof document === 'undefined' || !document.body) return null;
  return createPortal(layer, document.body);
}

export default Modal;
